<template>
  <div ref="trendEl" class="compliance-trend-bar"></div>
</template>
<script>
import * as echarts from "echarts";
import { formattedCurrency } from "./Utils";

/** Match ComplianceGauge — brand pink below target vs teal at/above. */
const BELOW_TARGET = "#ff1463";
const MEETS_TARGET = "#4EB888";
const TARGET_LINE = "#12253a";

/**
 * Each row: { label, totalSpend, indigenousSpend, totalAwards, indigenousAwards }
 * (from EntityProfile compliance donutFySpend — aligned with bar FY labels).
 */
export default {
  props: {
    labels: { type: Array, default: () => [] },
    values: { type: Array, default: () => [] },
    fyRows: { type: Array, default: () => [] },
    target: { type: Number, default: 4 },
    changed: { type: Number, default: 0 }
  },
  data() {
    return {
      chart: null,
      resizeObserver: null
    };
  },
  watch: {
    labels: {
      deep: true,
      handler() {
        this.scheduleRender();
      }
    },
    values: {
      deep: true,
      handler() {
        this.scheduleRender();
      }
    },
    fyRows: {
      deep: true,
      handler() {
        this.scheduleRender();
      }
    },
    target() {
      this.scheduleRender();
    },
    changed() {
      this.scheduleRender();
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.setupResizeObserver();
      this.renderChart();
    });
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  },
  methods: {
    scheduleRender() {
      this.$nextTick(() => {
        this.renderChart();
      });
    },
    setupResizeObserver() {
      const el = this.$refs.trendEl;
      if (!el || typeof ResizeObserver === "undefined") {
        return;
      }
      this.resizeObserver = new ResizeObserver(() => {
        if (this.chart) {
          this.chart.resize();
        }
      });
      this.resizeObserver.observe(el);
    },
    /** Resolve FY row for axis tooltip (axisValue is the category label). */
    getRowForTooltipParam(first) {
      if (!first) {
        return null;
      }
      const rows = this.fyRows || [];
      const axisCat =
        first.axisValue !== undefined && first.axisValue !== null
          ? String(first.axisValue)
          : "";
      let idx =
        typeof first.dataIndex === "number" && !Number.isNaN(first.dataIndex)
          ? first.dataIndex
          : -1;
      if (axisCat) {
        const byAxis = rows.find((r) => r && String(r.label) === axisCat);
        if (byAxis) {
          return byAxis;
        }
      }
      if (idx >= 0 && rows[idx]) {
        return rows[idx];
      }
      const nameCat = first.name != null ? String(first.name) : "";
      if (nameCat && nameCat !== "undefined") {
        const byName = rows.find((r) => r && String(r.label) === nameCat);
        if (byName) {
          return byName;
        }
      }
      if (axisCat && idx < 0) {
        idx = this.labels.indexOf(axisCat);
        if (idx >= 0 && rows[idx]) {
          return rows[idx];
        }
      }
      return null;
    },
    barDatumValue(p) {
      if (p == null || p.value === undefined || p.value === null) {
        return NaN;
      }
      if (typeof p.value === "number") {
        return p.value;
      }
      if (typeof p.value === "object" && p.value !== null && typeof p.value.value === "number") {
        return p.value.value;
      }
      return NaN;
    },
    renderChart() {
      const el = this.$refs.trendEl;
      if (!el) return;
      if (this.chart) {
        this.chart.dispose();
      }
      this.chart = echarts.init(el);

      const tgt = this.target;
      const barColors = this.values.map((v) =>
        v >= tgt ? MEETS_TARGET : BELOW_TARGET
      );

      const fmtTooltip = (params) => {
        const items = Array.isArray(params) ? params : [params];
        const first = items[0];
        const row = this.getRowForTooltipParam(first);
        const idx =
          typeof first.dataIndex === "number" && !Number.isNaN(first.dataIndex)
            ? first.dataIndex
            : -1;
        const axisCat =
          first.axisValue !== undefined && first.axisValue !== null
            ? String(first.axisValue)
            : "";
        const title =
          axisCat ||
          (idx >= 0 && this.labels[idx] ? String(this.labels[idx]) : "") ||
          (row && row.label ? String(row.label) : "");
        const pctFromBar =
          idx >= 0 && this.values[idx] !== undefined
            ? Number(this.values[idx])
            : NaN;
        const pctFromDatum = this.barDatumValue(first);
        const pct = Number.isFinite(pctFromBar)
          ? pctFromBar
          : Number.isFinite(pctFromDatum)
            ? pctFromDatum
            : row && row.totalSpend
              ? (Number(row.indigenousSpend) / Number(row.totalSpend)) * 100
              : 0;

        let volLine = "Volume: —";
        let amtLine = "Spend: —";
        if (row) {
          const volInd = Number(row.indigenousAwards);
          const volTot = Number(row.totalAwards);
          const amtInd = Number(row.indigenousSpend);
          const amtTot = Number(row.totalSpend);
          if (Number.isFinite(volInd) && Number.isFinite(volTot)) {
            volLine = `Volume: ${volInd} indigenous of ${volTot} total (by count)`;
          }
          if (Number.isFinite(amtInd) && Number.isFinite(amtTot)) {
            amtLine = `Spend: ${formattedCurrency(amtInd)} indigenous of ${formattedCurrency(amtTot)} total`;
          }
        }

        return (
          `<strong>${title}</strong><br/>` +
          `Indigenous share of spend: ${Number(pct).toFixed(2)}%<br/>` +
          `${volLine}<br/>` +
          `${amtLine}`
        );
      };

      const option = {
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
          formatter: fmtTooltip
        },
        grid: {
          top: 30,
          bottom: 30,
          left: 50,
          right: 20
        },
        xAxis: {
          type: "category",
          data: this.labels,
          axisLabel: {
            fontSize: 12,
            color: "#333"
          }
        },
        yAxis: {
          type: "value",
          axisLabel: {
            formatter: "{value}%",
            fontSize: 11,
            color: "#666"
          },
          min: 0
        },
        series: [
          {
            type: "bar",
            data: this.values.map((v, i) => ({
              value: v,
              itemStyle: { color: barColors[i] }
            })),
            barMaxWidth: 40,
            label: {
              show: true,
              position: "top",
              formatter: (p) => {
                const v = this.barDatumValue(p);
                return (Number.isFinite(v) ? v : 0).toFixed(1) + "%";
              },
              fontSize: 11,
              fontWeight: "bold",
              color: "#333"
            },
            markLine: {
              silent: true,
              symbol: "none",
              lineStyle: {
                color: TARGET_LINE,
                type: "dashed",
                width: 2
              },
              label: {
                formatter: "Target ({c}%)",
                position: "insideEndTop",
                fontSize: 11,
                color: TARGET_LINE
              },
              data: [{ yAxis: tgt }]
            }
          }
        ]
      };

      this.chart.setOption(option);

      this.$nextTick(() => {
        if (this.chart) {
          this.chart.resize();
        }
      });
    }
  }
};
</script>
<style scoped>
.compliance-trend-bar {
  width: 100%;
  height: 300px;
}
</style>
